// Unit type constants
export const UNIT_TYPES = {
  ENERGY: "energy",
  MASS: "mass",
  VOLUME: "volume",
  TIME: "time",
};

// Field configurations
export const FIELD_CONFIGS = {
  energyPrice: {
    key: "energyPrice",
    unitType: UNIT_TYPES.ENERGY,
    ranges: { hard: { min: 0, max: 10 } },
  },
  acidPrice: {
    key: "acidPrice",
    unitType: UNIT_TYPES.MASS,
    ranges: { hard: { min: 0, max: 100 } },
  },
  alkaliPrice: {
    key: "alkaliPrice",
    unitType: UNIT_TYPES.MASS,
    ranges: { hard: { min: 0, max: 100 } },
  },
  waterPrice: {
    key: "waterPrice",
    unitType: UNIT_TYPES.VOLUME,
    ranges: { hard: { min: 0, max: 10 } },
  },
  costofDownTime: {
    key: "costofDownTime",
    unitType: UNIT_TYPES.TIME,
    ranges: { hard: { min: 0, max: 100000 } },
  },
  additional_chemical_price: {
    key: "additional_chemical_price",
    unitType: UNIT_TYPES.MASS,
    ranges: { hard: { min: 0, max: 1 } },
  },
};

export const FIELD_ORDER = [
  "energyPrice",
  "acidPrice",
  "alkaliPrice",
  "waterPrice",
  "costofDownTime",
  "additional_chemical_price",
];

// Helper functions
const getUnitSuffix = (fieldKey, currency, volume, mass, getCurrencySymbol) => {
  const currencySymbol = getCurrencySymbol[currency];
  const config = FIELD_CONFIGS[fieldKey];

  if (config.unitType === UNIT_TYPES.MASS) {
    return `${currencySymbol}/${mass}`;
  } else if (config.unitType === UNIT_TYPES.VOLUME) {
    return `${currencySymbol}/${volume}`;
  } else if (config.unitType === UNIT_TYPES.ENERGY) {
    return `${currencySymbol}/kWh`;
  } else if (config.unitType === UNIT_TYPES.TIME) {
    return `${currencySymbol}/day`;
  }

  return "";
};

const getConvertedRanges = (
  fieldKey,
  conversion_rate,
  volume,
  mass,
  currency
) => {
  const config = FIELD_CONFIGS[fieldKey];
  let { min, max } = config.ranges.hard;

  // Apply unit conversions
  if (config.unitType === UNIT_TYPES.MASS && mass === "lb") {
    max *= 2.20462; // kg to lb conversion
  }
  if (config.unitType === UNIT_TYPES.VOLUME && volume === "kgal") {
    max *= 0.26417205; // m3 to kgal conversion
  }

  // Apply currency conversion
  if (currency !== "USD") {
    max *= conversion_rate;
  }

  // Format to 2 decimal places
  min = parseFloat(min.toFixed(2));
  max = parseFloat(max.toFixed(2));

  return { hard: { min, max } };
};

// Helper function to convert values based on unit changes
const getConvertedValue = (
  value,
  fieldKey,
  prevMass,
  prevVolume,
  prevCurrency,
  prevRate,
  newMass,
  newVolume,
  newCurrency,
  newRate
) => {
  if (!value || value === 0) return value;

  const config = FIELD_CONFIGS[fieldKey];
  let convertedValue = parseFloat(value);

  // Apply mass unit conversions
  if (config.unitType === UNIT_TYPES.MASS) {
    // Convert from previous unit to new unit
    if (prevMass === "kg" && newMass === "lb") {
      convertedValue *= 2.20462; // kg to lb
    } else if (prevMass === "lb" && newMass === "kg") {
      convertedValue /= 2.20462; // lb to kg
    }
  }

  // Apply volume unit conversions
  if (config.unitType === UNIT_TYPES.VOLUME) {
    if (prevVolume === "m3" && newVolume === "kgal") {
      convertedValue *= 0.26417205; // m3 to kgal
    } else if (prevVolume === "kgal" && newVolume === "m3") {
      convertedValue /= 0.26417205; // kgal to m3
    }
  }

  // Apply currency conversions
  if (prevCurrency !== newCurrency || prevRate !== newRate) {
    // First convert back to USD if needed
    if (prevCurrency !== "USD") {
      convertedValue /= prevRate;
    }
    // Then convert to new currency if needed
    if (newCurrency !== "USD") {
      convertedValue *= newRate;
    }
  }

  return parseFloat(convertedValue.toFixed(2));
};

export const COST_INFORMATION = (
  conversion_rate,
  currency,
  volume,
  mass,
  costValues = {}
) => {
  return Object.fromEntries(
    FIELD_ORDER.map((fieldKey) => [
      fieldKey,
      {
        key: fieldKey,
        ranges: getConvertedRanges(
          fieldKey,
          conversion_rate,
          volume,
          mass,
          currency
        ),
      },
    ])
  );
};

export const getFieldUnit = getUnitSuffix;
export const getConvertedFieldValue = getConvertedValue;
